import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import ProductHero, { ProductContainer } from '../components/product/ProductHero';
import VideoGallery from '../components/VideoGallery';
import PhotoSlide from '../components/PhotoSlide';
import { Products } from '../assets/products';
import SeoHead from '../components/SeoHead';
import NotFoundPage from './NotFoundPage';
import { SITE_ORIGIN } from '../config/site';

const ProductPage = () => {
  const { productSlug } = useParams();
  const [productData, setProductData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const loadProductData = async () => {
      // Map slug to product ID
      const slugToId = {
        'hrms': 1,
        'crm': 2,
        'insurance': 3,
        'revenues': 4,
        'expenses': 5,
        'inventory': 6,
        'clinic': 7,
        'pos': 8
      };
      
      const productId = slugToId[productSlug];
      if (!productId) {
        setLoading(false);
        return;
      }
      
      // const product = await fetchProductData(productId);
      const product = Products?.find((product) => product.id === productId);
      setProductData(product);
      setLoading(false);
    };

    loadProductData();
  }, [productSlug]);

  if (loading) {
    return <LoadingMessage>Loading product information...</LoadingMessage>;
  }

  if (!productData) {
    return <NotFoundPage />;
  }

  const ogImage = productData.backgroundImage
    ? `${SITE_ORIGIN}${productData.backgroundImage}`
    : undefined;
  
  return (
    <>
      <SeoHead
        title={productData.meta.title}
        description={productData.meta.description}
        keywords={productData.meta.keywords}
        path={`/products/${productSlug}`}
        image={ogImage}
      />
    <ProductContainer>      
      <ProductHero 
        product={productData}
      />
      {/* {productSlug === 'hrms' && (
        <PricingCallout>
          <div>
            <CalloutEyebrow>HRMS Pricing</CalloutEyebrow>
            <h2>See AKAS HRIS plans and support options</h2>
            <p>
              Compare Standard and Advanced HRMS pricing, setup fees, included modules,
              and support packages.
            </p>
          </div>
          <PricingButton to="/products/hrms-price">View HRMS Pricing</PricingButton>
        </PricingCallout>
      )} */}
      {productData?.videos && (
        <VideoSection>
          <SectionTitle>Product Videos</SectionTitle>
          <VideoGallery videos={productData.videos} />
        </VideoSection>
      )}

      <FeaturesSection>
        <SectionTitle>Key Features</SectionTitle>
        <FeaturesGrid>
          {productData.mainFeatures?.map((feature, index) => (
            <FeatureCard key={index}>
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureContent>
                <h3>{feature.title}</h3>
                <FeatureDescription>{feature.description}</FeatureDescription>
                <div>
                  <PhotoSlide
                    images={feature.images}
                  />
                </div>
              </FeatureContent>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </FeaturesSection>

      {productData.benefits && (
        <BenefitsSection>
          <SectionTitle>Benefits for Your Business</SectionTitle>
          <BenefitsContainer>
            {productData.benefits.map((benefit, index) => (
              <BenefitItem key={index}>
                <BenefitNumber>{benefit.number}</BenefitNumber>
                <BenefitContent>
                  <h3>{benefit.title}</h3>
                  <BenefitDescription>{benefit.description}</BenefitDescription>
                  
                </BenefitContent>
              </BenefitItem>
            ))}
          </BenefitsContainer>
        </BenefitsSection>
      )}

      {productData.additionalFeatures && (
        <AdditionalFeaturesSection>
        <SectionTitle>Additional Features</SectionTitle>
        <AdditionalFeaturesGrid>
          {productData.additionalFeatures?.map((feature, index) => (
            <AdditionalFeatureCard key={index}>
              <AdditionalFeatureIcon>{feature.icon}</AdditionalFeatureIcon>
              <h3>{feature.title}</h3>
              {feature.description && <AdditionalFeatureDescription>{feature.description}</AdditionalFeatureDescription>}
            </AdditionalFeatureCard>
          ))}
        </AdditionalFeaturesGrid>
      </AdditionalFeaturesSection>
      )}

      <CTASection>
        <h2>Ready to transform your business?</h2>
        <p>Contact us today to schedule a personalized demo and learn how our solution can benefit your business.</p>
        <CTAButton to="/contact">Contact Us</CTAButton>
      </CTASection>
    </ProductContainer>
    </>
  );
};

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.2rem;
  color: var(--text-dark);
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.2rem;
  color: #d32f2f;
`;

const PricingCallout = styled.section`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  border: 1px solid rgba(0, 142, 204, 0.18);
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(0, 142, 204, 0.08), #ffffff);
  padding: 1.35rem;
  margin: 0 0 2rem;

  h2 {
    color: #111827;
    font-size: 1.35rem;
    margin-bottom: 0.4rem;
  }

  p {
    color: #4b5563;
    margin: 0;
    line-height: 1.6;
  }

  @media (max-width: 768px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const CalloutEyebrow = styled.span`
  display: inline-block;
  color: var(--primary-color);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 0.35rem;
`;

const PricingButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0.72rem 1.1rem;
  background: var(--primary-color);
  color: #fff;
  border-radius: 999px;
  font-weight: 700;
  transition: background-color 0.2s ease, transform 0.2s ease;

  &:hover {
    background: #0077ab;
    transform: translateY(-1px);
  }
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  color: var(--primary-color);
  text-align: center;
  margin-bottom: 2rem;
`;

const VideoSection = styled.section`
  margin: 1.2rem 0;
  text-align: center;
`;

const FeaturesSection = styled.section`
  margin: 2rem 0;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const FeatureCard = styled.div`
  background-color: var(--primary-light);
  border-radius: 8px;
  padding: 1.5rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const FeatureIcon = styled.div`
  font-size: 2rem;
  background-color: var(--primary-color);
  color: white;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  flex-shrink: 0;
`;

const FeatureContent = styled.div`
  h3 {
    font-size: 1.3rem;
    margin-bottom: 0.75rem;
    color: var(--primary-color);
  }
  
  p {
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.5;
    color: var(--text-dark);
  }
`;

const FeatureDescription = styled.div`
  color: var(--text-color);
  margin-bottom: 1.5rem;
`;

const AdditionalFeaturesSection = styled.section`
  margin: 2rem 0;
  text-align: center;
`;

const AdditionalFeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const AdditionalFeatureCard = styled.div`
  background-color: var(--primary-light);
  color: var(--text-dark);
  padding: 1.5rem;
  border-radius: 8px;
  text-align: center;
  position: relative;
  padding-top: 3rem;
  border: 1px solid var(--primary-color);
  
  h3 {
    font-size: 1.2rem;
    margin-bottom: 0.5rem;
    color: var(--primary-color);
  }
  
  p {
    font-size: 0.9rem;
    margin: 0;
  }
`;

const AdditionalFeatureIcon = styled.div`
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 60px;
  background-color: var(--primary-color);
  border: 2px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  color: white;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const BenefitsSection = styled.section`
  margin: 2rem 0;
`;

const AdditionalFeatureDescription = styled.div`
  color: var(--text-color);
  margin-bottom: 1.5rem;
`;

const BenefitsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(500px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitItem = styled.div`
  display: flex;
  gap: 1.5rem;
`;

const BenefitNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--accent-color);
  line-height: 1;
`;

const BenefitContent = styled.div`
  h3 {
    font-size: 1.3rem;
    margin-bottom: 0.75rem;
    color: var(--primary-color);
  }
  
  p {
    color: var(--text-dark);
    line-height: 1.6;
  }
`;

const BenefitDescription = styled.div`
  color: var(--text-color);
  margin-bottom: 1.5rem;
`;

const CTASection = styled.div`
  background-color: var(--primary-color);
  color: white;
  padding: 3rem;
  text-align: center;
  border-radius: 10px;
  margin-top: 2rem;
  
  h2 {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const CTAButton = styled(Link)`
  display: inline-block;
  padding: 0.75rem 2rem;
  background-color: white;
  color: var(--primary-color);
  font-weight: 500;
  border-radius: 40px;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }
`;

export default ProductPage; 